import { useFeature } from '@/hooks/useFeature';
import { FeatureGate as FeatureGateType, FeatureKey } from '@/lib/featureGates';
import { cn } from '@/lib/utils';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface FeatureGateProps {
  feature: FeatureKey;
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Hide-mode wrapper: renders children only if the feature is allowed.
 * For "disable + tooltip" UX, use the `useFeature` hook directly so the call-site
 * keeps full control over the disabled control's appearance and tooltip text.
 */
export const FeatureGate = ({ feature, children, fallback = null }: FeatureGateProps) => {
  const { allowed } = useFeature(feature);
  return <>{allowed ? children : fallback}</>;
};

interface UpgradeBadgeProps {
  gate: FeatureGateType;
  className?: string;
}

export const UpgradeBadge = ({ gate, className }: UpgradeBadgeProps) => {
  const { t } = useTranslation('featureGate');

  const label = gate.minProduct === 'PRO'
    ? t('requirement.pro')
    : gate.minStorageBytes != null && gate.minStorageBytes >= 1_000_000_000_000
      ? t('requirement.business')
      : gate.minStorageBytes != null && gate.minStorageBytes >= 500_000_000_000
        ? t('requirement.team')
        : t('requirement.paid');

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400',
        className,
      )}>
      {label}
    </span>
  );
};
